import React, { useCallback, useEffect, useRef } from "react";
import { Table, Placeholder, Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FaArrowLeft } from "react-icons/fa";
import { analyticsActions } from "../../actions/analytics.actions";

const EXECUTIONS_PAGE_SIZE = 50;

const getDateRange = (rangeType) => {
  const now = new Date();
  let startDate, endDate;

  switch (rangeType) {
    case "24hours":
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case "lastWeek":
      // Get last Sunday
      const lastSunday = new Date(now);
      lastSunday.setDate(now.getDate() - now.getDay());
      lastSunday.setHours(0, 0, 0, 0);

      // Start of last week (Sunday 7 days before last Sunday)
      startDate = new Date(lastSunday);
      startDate.setDate(lastSunday.getDate() - 7);

      // End of last week (Saturday = last Sunday - 1 day)
      endDate = new Date(lastSunday);
      endDate.setDate(lastSunday.getDate() - 1);
      endDate.setHours(23, 59, 59, 999);
      break;
    case "thisMonth":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = now;
      break;
    case "lastMonth":
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case "oneMonth":
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case "3months":
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case "6months":
      startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case "ytd":
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = now;
      break;
    case "1year":
      startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    default:
      return { startDate: null, endDate: null };
  }

  // Add 1 day to endDate to include the entire end day (backend parses date-only strings as midnight)
  const adjustedEndDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: adjustedEndDate.toISOString().split('T')[0]
  };
};

function RunbookExecutionsList(props) {
  const dispatch = useDispatch();
  const analyticsReducer = useSelector((state) => state.analytics);

  // Base query params (without cursor) captured from the first-page load so
  // "load more" can reuse them and only append the cursor.
  const baseParamsRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!props.user || !props.runbook) return;

    // Use clientId prop if provided (for super user viewing specific client),
    // otherwise use user's clientId
    const clientId = props.clientId || props.user.clientId;
    if (!clientId) return;

    const queryParams = {
      clientId: clientId,
      clientGuid: props.user.clientGuid,
      runbookId: props.runbook.runbookId,
      pageSize: EXECUTIONS_PAGE_SIZE,
    };

    const { startDate, endDate } = getDateRange(props.dateRange);
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;

    baseParamsRef.current = queryParams;
    // First page replaces the list (append defaults to false).
    dispatch(analyticsActions.getRunbookExecutions(queryParams));
    // eslint-disable-next-line
  }, [props.user, props.dateRange, props.runbook, props.clientId]);

  const executions = analyticsReducer.runbookExecutions || [];
  const loading = analyticsReducer.loadingRunbookExecutions;
  const loadingMore = analyticsReducer.loadingMoreRunbookExecutions;
  const hasMore = analyticsReducer.runbookExecutionsHasMore;
  const nextCursor = analyticsReducer.runbookExecutionsNextCursor;

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore || !nextCursor) return;
    const base = baseParamsRef.current;
    if (!base) return;
    dispatch(
      analyticsActions.getRunbookExecutions(
        { ...base, cursor: nextCursor },
        { append: true }
      )
    );
  }, [dispatch, hasMore, loading, loadingMore, nextCursor]);

  // Infinite scroll: fetch the next page when the sentinel scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  return (
    <div>
      <Button variant="outline-secondary" size="sm" onClick={props.onBack} className="mb-3">
        <FaArrowLeft className="me-2" />
        Back to Overview
      </Button>

      <h5 className="mb-3">
        Executions for: <strong>{props.runbook?.runbookName}</strong> ({props.runbook?.runbookId})
      </h5>
      <p className="text-muted">Click on an execution to view detailed API call information</p>

      {loading && (
        <Placeholder as={"div"} animation="glow">
          <Placeholder xs={12} size="lg" style={{ height: "300px" }} />
        </Placeholder>
      )}

      {!loading && executions.length > 0 && (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Execution ID</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Status</th>
                <th>Trigger</th>
                <th>Total API Calls</th>
                <th>Successful Calls</th>
                <th>Failed Calls</th>
                <th>Success Rate</th>
                <th>Records</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((execution, index) => (
                <tr
                  key={execution.executionId ?? index}
                  onClick={() => props.onExecutionClick(execution)}
                  style={{ cursor: "pointer" }}
                  className="hover-row"
                >
                  <td><code>{execution.executionId}</code></td>
                  <td>{new Date(execution.startTime).toLocaleString()}</td>
                  <td>{new Date(execution.endTime).toLocaleString()}</td>
                  <td>{execution.status}</td>
                  <td>{execution.executionTrigger}</td>
                  <td>{execution.totalApiCalls}</td>
                  <td>{execution.successfulApiCalls}</td>
                  <td>{execution.failedApiCalls}</td>
                  <td>{execution.successRate.toFixed(2)}%</td>
                  <td>{execution.totalRecordsReturned.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Sentinel observed for infinite scroll; Load more is the manual fallback. */}
          <div ref={sentinelRef} />

          {loadingMore && (
            <div className="text-center text-muted py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading more executions…
            </div>
          )}

          {hasMore && !loadingMore && (
            <div className="text-center py-3">
              <Button variant="outline-primary" size="sm" onClick={loadMore}>
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      {!loading && executions.length === 0 && (
        <div className="text-center text-muted py-5">
          <p>No executions found for this runbook in the selected date range</p>
        </div>
      )}
    </div>
  );
}

export default RunbookExecutionsList;
